/**
 * Certificado de garantía en PDF (carta): logo de la empresa, datos de la
 * venta, el texto de la política (descripción), cobertura, piezas y plan de
 * mantenimientos. Todo sale de la COPIA guardada en la garantía al vender,
 * así el certificado no cambia si después se edita la política.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtFecha, fechaFin, calendario, coberturaPiezas } from "./calendario";
import { fmtPrecio, CONDICION } from "./comunes";

const AZUL = "#18586b";
const MARGEN = 20;
const LOGO_ALTO_MAX = 24;
const LOGO_ANCHO_MAX = 60;

/** Logo (data URL de configuración) arriba a la izquierda; devuelve el alto usado. */
function dibujarLogo(doc, logo, y) {
  if (typeof logo !== "string" || !logo.startsWith("data:image/")) return 0;
  try {
    const props = doc.getImageProperties(logo);
    const escala = Math.min(LOGO_ANCHO_MAX / props.width, LOGO_ALTO_MAX / props.height);
    const w = props.width * escala;
    const h = props.height * escala;
    doc.addImage(logo, props.fileType || "PNG", MARGEN, y, w, h);
    return h;
  } catch (e) {
    return 0; // imagen ilegible: el certificado sale sin logo
  }
}

/** Arma el certificado y devuelve el documento jsPDF. */
export function generarCertificado({ garantia, politica, empresa = "", logo = "", hoy }) {
  const doc = new jsPDF({ unit: "mm", format: "letter" });
  const ancho = doc.internal.pageSize.getWidth();
  const alto = doc.internal.pageSize.getHeight();
  let y = 12;

  // Salta de página si lo que sigue no cabe.
  const espacio = (mm) => {
    if (y + mm > alto - MARGEN) {
      doc.addPage();
      y = MARGEN;
    }
  };

  // Con logo: logo a la izquierda y título + empresa alineados a la derecha
  // en la misma franja. Sin logo: título centrado como siempre.
  const altoLogo = dibujarLogo(doc, logo, y);
  const xTitulo = altoLogo ? ancho - MARGEN : ancho / 2;
  const alinear = altoLogo ? "right" : "center";
  const inicio = y;
  y = altoLogo ? inicio + altoLogo / 2 + (empresa ? -1 : 2) : inicio + 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("CERTIFICADO DE GARANTÍA", xTitulo, y, { align: alinear });
  if (empresa) {
    y += 7;
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(empresa, xTitulo, y, { align: alinear });
  }
  if (altoLogo) y = Math.max(y, inicio + altoLogo);

  y += 12;
  const fila = (etiqueta, valor) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text(etiqueta, MARGEN, y);
    doc.setFont("helvetica", "normal");
    doc.text(String(valor ?? ""), 60, y);
    y += 6;
  };
  fila("Cliente:", `${garantia.cliente}  ·  ${garantia.cedula}`);
  fila(
    "Vehículo:",
    `${garantia.marca} ${garantia.modelo} ${garantia.color} (${CONDICION[garantia.condicion]})`
  );
  fila("Chasis:", garantia.chasis);
  fila("Fecha de venta:", fmtFecha(garantia.fechaVenta));
  const piezas = coberturaPiezas(garantia, hoy);
  fila(
    "Vigencia:",
    piezas.length
      ? `Limitada a piezas, hasta el ${fmtFecha(fechaFin(garantia))} (la que mas dura)`
      : `${garantia.meses} meses, hasta el ${fmtFecha(fechaFin(garantia))}`
  );

  // Párrafo con salto de página línea a línea (la descripción puede ser larga).
  const parrafo = (titulo, texto, { tamano = 10 } = {}) => {
    if (!String(texto || "").trim() && !titulo) return;
    doc.setFontSize(tamano);
    if (titulo) {
      espacio(10);
      doc.setFont("helvetica", "bold");
      doc.text(titulo, MARGEN, y);
      y += 5;
    }
    doc.setFont("helvetica", "normal");
    const lineas = doc.splitTextToSize(String(texto || "").trim() || "—", ancho - MARGEN * 2);
    lineas.forEach((linea) => {
      espacio(5);
      doc.text(linea, MARGEN, y);
      y += 5;
    });
    y += 3;
  };

  // Texto libre de la política (condiciones, compromisos, contacto...).
  if (String(garantia.descripcion || "").trim()) {
    y += 4;
    parrafo(null, garantia.descripcion);
  }

  if (politica) {
    y += 2;
    parrafo("Cubre:", politica.cubre);
    parrafo("No cubre:", politica.noCubre);
  }

  const tabla = (titulo, head, body) => {
    espacio(20);
    y += 2;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(titulo, MARGEN, y);
    autoTable(doc, {
      startY: y + 2,
      margin: { left: MARGEN, right: MARGEN },
      headStyles: { fillColor: AZUL },
      styles: { fontSize: 9 },
      head: [head],
      body,
    });
    y = doc.lastAutoTable.finalY + 6;
  };

  // Garantía limitada: solo las piezas listadas, cada una con su plazo.
  if (piezas.length) {
    tabla(
      "Piezas cubiertas",
      ["Pieza", "Meses", "Cubierta hasta"],
      piezas.map((x) => [x.nombre, x.meses, fmtFecha(x.vence)])
    );
  }

  tabla(
    "Plan de mantenimientos",
    ["#", "Servicio", "Fecha", "Ventana", "Km ref.", "Precio"],
    calendario(garantia, [], hoy).map((s) => [
      s.numero,
      s.nombre,
      fmtFecha(s.fecha),
      `${fmtFecha(s.desde)} – ${fmtFecha(s.hasta)}`,
      Number(s.kmRef || 0).toLocaleString("es-DO"),
      fmtPrecio(s.precio),
    ])
  );

  y += 2;
  parrafo(
    null,
    "Los mantenimientos deben realizarse dentro de la ventana indicada. Omitir un servicio puede " +
      "ocasionar la pérdida de la garantía. El kilometraje es una referencia.",
    { tamano: 9 }
  );

  // Las firmas pueden bajar más que el texto (margen inferior de 10 mm).
  if (y + 20 > alto - 10) {
    doc.addPage();
    y = MARGEN;
  }
  y += 14;
  doc.setFontSize(9);
  doc.line(MARGEN, y, 90, y);
  doc.line(ancho - 90, y, ancho - MARGEN, y);
  doc.text("Firma del cliente", 55, y + 5, { align: "center" });
  doc.text("Por la empresa", ancho - 55, y + 5, { align: "center" });

  return doc;
}

export function abrirCertificado(datos) {
  window.open(generarCertificado(datos).output("bloburl"), "_blank");
}
